import {
  AllergyWarning, Badge, ChildAvatar, ErrorState, LoadingState, PresenceLabel,
  Row, Stack, useApi, useI18n,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { presenceOf, useMyChild } from '../myChildren';

/**
 * The child's home dashboard (FR-HOME-01 … FR-HOME-05).
 *
 * Name, photograph, today's presence, and five large actions — exactly the five the client drew:
 * يومي اليوم · الصور · الرسائل · المدفوعات · معلومات الطفل. The layout is deliberately shallow; a parent
 * opens one of these and nothing else.
 */
export function ChildHome({ childId }: { childId: number }) {
  const api = useApi();
  const { t } = useI18n();

  const child = useQuery({
    queryKey: ['child', childId],
    queryFn: () => api.children.get(childId),
  });
  const mine = useMyChild(childId);

  // Only the count: the badge says an incident is waiting to be read and acknowledged (FR-CHD-12).
  const unread = useQuery({
    queryKey: ['incidents', 'child', childId, 'unacknowledged'],
    queryFn: () => api.health.incidents({ childId, unacknowledged: true, pageSize: 1 }),
  });

  if (child.isPending) return <LoadingState />;
  if (child.isError) return <ErrorState error={child.error} onRetry={() => void child.refetch()} />;

  const data = child.data;
  const name = data.preferredName
    ? `${data.preferredName} ${data.lastName}`
    : `${data.firstName} ${data.lastName}`;

  return (
    <Stack>
      <Row>
        <ChildAvatar child={data} photoUrl={data.photoUrl} allergies={data.allergies} />
        <span className="dg-stack dg-stack--tight">
          <h1 style={{ fontSize: 'var(--dg-text-xl)' }}>{name}</h1>
          {data.className && <Badge>{data.className}</Badge>}
        </span>
      </Row>

      {/* Today's attendance, by the rule the chooser and the staff board share (FR-HOME-02). */}
      {mine && (
        <div className="dg-card">
          <PresenceLabel presence={presenceOf(mine)} gender={data.gender} />
        </div>
      )}

      <AllergyWarning allergies={data.allergies} />

      <nav className="dg-primary-actions" aria-label={t('a11y.mainNavigation')}>
        <PrimaryAction to={`/child/${childId}/day`} icon="📔" label={t('home.day')} hint={t('home.day.hint')} />
        <PrimaryAction to={`/child/${childId}/photos`} icon="📸" label={t('home.photos')} hint={t('home.photos.hint')} />
        <PrimaryAction to={`/child/${childId}/messages`} icon="💬" label={t('home.messages')} hint={t('home.messages.hint')} />

        {/* Every guardian of the child sees it (FR-HOME-05, Q42). */}
        <PrimaryAction to={`/child/${childId}/payments`} icon="💳" label={t('home.payments')} hint={t('home.payments.hint')} />

        <PrimaryAction
          to={`/child/${childId}/info`}
          icon="ℹ️"
          label={t('home.info')}
          hint={t('home.info.hint')}
        />

        {/* On the home screen rather than buried in a menu. A parent who has decided to complain should
            not also have to go looking for where (§4.14). */}
        <PrimaryAction
          to={`/child/${childId}/complaints`}
          icon="🗒️"
          label={t('home.complaints')}
          hint={t('home.complaints.hint')}
        />

        {/* Every incident inside the guardian's history window, each opening the record to acknowledge
            (FR-CHD-12, FR-CHD-14). */}
        <PrimaryAction
          to={`/child/${childId}/incidents`}
          icon="🩹"
          label={t('home.incidents')}
          hint={t('home.incidents.hint')}
          badge={unread.data?.totalCount}
        />

        {/* Also in the ☰ menu, but the home screen is where a guardian starts, so it is a way in to the record and to
            telling the nursery about an absence too (FR-ATT-11). */}
        <PrimaryAction
          to={`/child/${childId}/attendance`}
          icon="✅"
          label={t('home.attendance')}
          hint={t('home.attendance.hint')}
        />

        {/* Closures and trips, before the notification about them is the only place they are (FR-MSG-09). */}
        <PrimaryAction
          to={`/child/${childId}/calendar`}
          icon="🗓️"
          label={t('home.calendar')}
          hint={t('home.calendar.hint')}
        />
      </nav>

      <Link className="dg-button dg-button--ghost" to="/">{t('children.switch')}</Link>
    </Stack>
  );
}

function PrimaryAction({ to, icon, label, hint, badge, wide }: {
  to: string; icon: string; label: string; hint: string; badge?: number; wide?: boolean;
}) {
  return (
    <Link className={`dg-primary-action${wide ? ' dg-primary-action--wide' : ''}`} to={to}>
      <span className="dg-primary-action__icon" aria-hidden="true">{icon}</span>
      <span>{label}</span>
      <span className="dg-card__hint" style={{ fontWeight: 400 }}>{hint}</span>
      {/* An unread count on the button itself, so a parent sees what is new without opening each one
          (FR-HOME-04). */}
      {badge ? <span className="dg-primary-action__badge"><Badge tone="count">{badge}</Badge></span> : null}
    </Link>
  );
}
